import React, { useState, useEffect, useMemo, useRef, useCallback, useLayoutEffect } from 'react';
import { usePageSearchState, matchesSearch, noMatchText } from '../lib/pageSearch';
import { useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  getSyncJobCostCurrentSummary,
  getSyncJobCostSummary,
  getSyncJobCostPhases,
  getSyncJobCostsCurrent,
  getSyncJobCostMonthlySummary,
  getSyncJobCostArchivedSummary,
  getProjects,
  getCostCodeNotes,
  addCostCodeNote,
  getArJobCollections,
  getJobHoursCurrent,
  getJobHoursMonthly,
  getArInvoicedMonthly,
  getProjectSummary,
  getJobCostContracts,
  setJobCostContract,
} from '../api';
import { can } from '../permissions';
import LoadingState from '../components/LoadingState';
import StateScreen from '../components/ui/StateScreen';
import SegmentedControl from '../components/ui/SegmentedControl';
import { PsrExportMenu } from '../components/jobcost/PsrExportMenu';
import { groupByContractYear } from '../components/dashboard/projectUtils';
import {
  fmt$, fmtPct, spentColor, overTone, filterBucket, healthKey,
  HealthBadge, KpiCards, MonthlyChart, MonthlyLegend, MonthlyNote, PhaseTable, BudgetBar, SyncChip, JcIcon,
  applyBudgetAssumption, buildPhases, buildMonthly, buildFinancials, isWarrantyJob,
} from '../components/jobcost/JobCostShared';

/* ═══════════════════════════════════════════════════════════════════════════
   Job Costs dashboard (/job-costs) — warm-cream + sage redesign
   Portfolio-wide budget vs. cost across all ComputerEase jobs. Token-only
   colors. Visual reskin — CE-sync API, SSE live-sync, assumed-at-budget logic,
   and filter/sort all unchanged.
   ═══════════════════════════════════════════════════════════════════════════ */

const ratioOf = (j) => {
  const b = parseFloat(j.total_budget || 0);
  return b > 0 ? parseFloat(j.total_cost || 0) / b : null;
};
// Health-tier rank for the "Most Over Budget" sort so the badges group cleanly
// (over-budget → at-risk → on-track → under-budget → no-budget) instead of a pure
// dollar-overrun order intermixing tiers — a big under-budget job would otherwise
// outrank a small at-risk one, and no-budget jobs (budget 0 → positive overrun)
// would slip in among the over-budget ones. Uses the same healthKey as the badges.
const HEALTH_RANK = { over: 0, atbudget: 1, at: 2, on: 3, under: 4, none: 5 };
const healthRank = (j) => HEALTH_RANK[healthKey(ratioOf(j))] ?? 4;
const overrunOf = (j) => parseFloat(j.total_cost || 0) - parseFloat(j.total_budget || 0);

const normalizeNote = (n) => ({
  // GET/POST /api/cost-code-notes send the author as user_name.
  author: n.user_name || n.author || n.created_by || n.changed_by || n.user || '—',
  text: n.note || n.text || '',
  date: n.created_at || n.date || n.changed_at || null,
});

/* tokenized sort select */
const SortSelect = ({ value, onChange, options }) => (
  <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="jc-sort"
      style={{ appearance: 'none', WebkitAppearance: 'none', height: 32, padding: '0 32px 0 12px', borderRadius: 8, minWidth: 172, fontSize: 14, fontWeight: 500, color: 'var(--text-1)', fontFamily: 'var(--font-sans)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer', letterSpacing: '-0.005em' }}
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    <span style={{ position: 'absolute', right: 10, color: 'var(--text-3)', pointerEvents: 'none', display: 'inline-flex' }}><JcIcon name="chevron-d" size={14} /></span>
  </div>
);

/* portfolio KPI row — "Quiet Ledger": one calm surface panel, values neutral,
   color reserved for the figures that carry meaning (Committed = amber, matching
   the page-wide committed language; Net Over/Under = green under / red over).
   Collapses to a neutral card grid on narrow widths (see .pkr* CSS below). */
// A signed percent, e.g. +1.1% / -19.1% — for over/under, where the sign is the point.
const fmtSignedPct = (ratio) => (ratio == null ? '—' : `${ratio > 0 ? '+' : ''}${fmtPct(ratio * 100)}`);

const KpiTile = ({ label, value, sub, valueColor, muted, valueSize = 26, variant }) => (
  <div className={'pkr-tile' + (variant ? ' pkr-tile-' + variant : '')} style={{ display: 'flex', flexDirection: 'column', gap: 9, flex: '1 1 180px', minWidth: 150, padding: '16px 20px' }}>
    <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.075em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{label}</div>
    <div className={'mono ' + (valueSize >= 30 ? 'pkr-hero-lg' : 'pkr-hero')} style={{ fontSize: valueSize, fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums', minWidth: 0, overflowWrap: 'anywhere', color: muted ? 'var(--text-4)' : (valueColor || 'var(--text-1)') }}>{value}</div>
    <div className="mono" style={{ marginTop: 'auto', minHeight: 16, fontSize: 12, lineHeight: 1.35, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{sub || ' '}</div>
  </div>
);
const KpiDivider = () => <div className="pkr-div" aria-hidden="true" style={{ flex: '0 0 auto', width: 1, alignSelf: 'stretch', margin: '14px 0', background: 'var(--border-subtle)' }} />;

/* Combined ledger tile — Budget / Spent / Committed folded into one calm tile
   (three labeled figures) so the row has room for the gross-margin tiles. Budget +
   Spent stay neutral (an aggregate spend ratio isn't per-job "risk", so it isn't
   ramped); Committed keeps the page-wide amber. */
const LedgerRow = ({ label, value, color, hint, onValueHover, overlay }) => {
  const valueEl = (
    <span className="mono pkr-ledger" style={{ fontSize: 16.5, fontWeight: 700, color: color || 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', textAlign: 'right', minWidth: 0, overflowWrap: 'anywhere', ...(onValueHover ? { cursor: 'help', textDecoration: 'underline dotted', textUnderlineOffset: 3 } : null) }}>
      {value}{hint ? <span style={{ color: 'var(--text-4)', fontWeight: 600, marginLeft: 6, whiteSpace: 'nowrap' }}>{hint}</span> : null}
    </span>
  );
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, minWidth: 0 }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-3)', whiteSpace: 'nowrap', flex: '0 0 auto' }}>{label}</span>
      {onValueHover ? (
        // Only the number is the hover target; the overlay sits in this box so it
        // anchors right under the number and inherits the page font, not mono.
        <span onMouseEnter={() => onValueHover(true)} onMouseLeave={() => onValueHover(false)} style={{ position: 'relative', minWidth: 0, textAlign: 'right' }}>
          {valueEl}
          {overlay}
        </span>
      ) : valueEl}
    </div>
  );
};
const LedgerTile = ({ budget, cost, committed, spentPct, empty }) => (
  <div className="pkr-tile" style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: '1.55 1 214px', minWidth: 172, padding: '16px 20px' }}>
    <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.075em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Budget · Spent · Committed</div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <LedgerRow label="Budget" value={empty ? '—' : fmt$(budget)} color={empty ? 'var(--text-4)' : undefined} />
      <LedgerRow label="Spent" value={empty ? '—' : spentPct != null ? fmtPct(spentPct * 100) : fmt$(cost)} color={empty ? 'var(--text-4)' : undefined} hint={!empty && spentPct != null ? fmt$(cost) : null} />
      <LedgerRow label="Committed" value={committed ? fmt$(committed) : '—'} color={committed ? 'var(--warning)' : 'var(--text-4)'} />
    </div>
  </div>
);

/* Combined gross-margin tile — the % and $ (plus the contract they're a share of)
   folded into one calm ledger, parallel to Budget · Spent · Committed. Margin rows
   are sign-colored (green ≥0, red <0); contract is neutral. Blended/revenue-weighted
   (see totals memo); reads "–" when the filtered jobs carry no contract value. */
const HintLine = ({ label, value, color, strong, top }) => (
  <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 14, padding: '6px 14px', borderTop: top ? '1px solid var(--border-subtle)' : 'none' }}>
    <span style={{ fontSize: 13, fontWeight: strong ? 600 : 500, color: strong ? 'var(--text-1)' : 'var(--text-3)', whiteSpace: 'nowrap' }}>{label}</span>
    <span className="mono" style={{ fontSize: 13.5, fontWeight: strong ? 800 : 600, color: color || 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>{value}</span>
  </div>
);
const MarginHint = ({ gm, gmPct, contract, gmCost, gmCommitted, gmJobs, noContractJobs, color }) => (
  <div role="tooltip" style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 40, width: 330, maxWidth: 'calc(100vw - 48px)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-md)', overflow: 'hidden', textAlign: 'left', pointerEvents: 'none' }}>
    <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)', fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>
      How gross margin is figured
    </div>
    <div style={{ padding: '6px 0' }}>
      <HintLine label="Contract" value={fmt$(contract)} />
      <HintLine label="− Spent to date" value={fmt$(-gmCost)} />
      <HintLine label="− Committed (open POs)" value={fmt$(-gmCommitted)} color="var(--warning)" />
      <HintLine top strong label="= Margin $" value={fmt$(gm, { signed: true })} color={color} />
      <HintLine strong label="Margin $ ÷ Contract" value={gmPct != null ? fmtPct(gmPct * 100) : '–'} color={color} />
    </div>
    <div style={{ padding: '9px 14px', borderTop: '1px solid var(--border-subtle)', fontSize: 12.5, lineHeight: 1.45, color: 'var(--text-3)' }}>
      Counts the {gmJobs} job{gmJobs === 1 ? '' : 's'} with a contract entered{noContractJobs > 0 ? `; ${noContractJobs} without one ${noContractJobs === 1 ? 'is' : 'are'} left out` : ''}.
    </div>
  </div>
);
/* #16: a job card's gross margin vs its target, popped up instantly while the mouse is over
   the Gross Margin column. Styled like MarginHint. Fixed-positioned in a portal, because the
   card header scrolls sideways on narrow screens and would clip it, and portalled into .app
   (not body) so the dark theme's colours apply. */
const TargetHint = ({ pos, actualPct, targetPct, actualColor }) => {
  const delta = actualPct - targetPct;
  const on = Math.abs(delta) < 0.0005;
  const toneColor = on ? 'var(--text-1)' : delta > 0 ? 'var(--success)' : 'var(--danger)';
  return createPortal(
    <div role="tooltip" style={{ position: 'fixed', top: pos.top, right: pos.right, zIndex: 10050, width: 300, maxWidth: 'calc(100vw - 16px)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-md)', overflow: 'hidden', textAlign: 'left', pointerEvents: 'none' }}>
      <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)', fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>
        Gross margin vs target
      </div>
      <div style={{ padding: '6px 0' }}>
        <HintLine label="Target (contract − budget)" value={fmtPct(targetPct * 100)} />
        <HintLine label="Actual (contract − spent − POs)" value={fmtPct(actualPct * 100)} color={actualColor} />
        <HintLine top strong label={on ? 'On target' : delta > 0 ? 'Above target by' : 'Below target by'} value={`${fmtPct(Math.abs(delta) * 100).replace('%', '')} pts`} color={toneColor} />
      </div>
    </div>,
    document.querySelector('.app') || document.body,
  );
};
const GrossMarginTile = ({ gm, gmPct, contract, hasContract, color, gmCost, gmCommitted, gmJobs, noContractJobs }) => {
  const [hover, setHover] = useState(false); // hovering the Margin % number
  return (
    <div className="pkr-tile" style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: '1.55 1 214px', minWidth: 172, padding: '16px 20px' }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.075em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Gross Margin</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <LedgerRow label="Margin %" value={hasContract && gmPct != null ? fmtPct(gmPct * 100) : '–'} color={hasContract ? color : 'var(--text-4)'}
          onValueHover={hasContract ? setHover : undefined}
          overlay={hasContract && hover ? <MarginHint gm={gm} gmPct={gmPct} contract={contract} gmCost={gmCost} gmCommitted={gmCommitted} gmJobs={gmJobs} noContractJobs={noContractJobs} color={color} /> : null} />
        <LedgerRow label="Margin $" value={hasContract ? fmt$(gm, { signed: true }) : '–'} color={hasContract ? color : 'var(--text-4)'} />
        <LedgerRow label="Contract" value={hasContract ? fmt$(contract) : '–'} color={hasContract ? undefined : 'var(--text-4)'} />
      </div>
    </div>
  );
};

/* Net Over/Under sub — health counts; bold + tinted only when non-zero, zeros faint. */
const NetCount = ({ n, label, color }) => (
  <span style={{ color: n > 0 ? color : 'var(--text-4)', fontWeight: n > 0 ? 700 : 500 }}>{n} {label}</span>
);

const PortfolioKpiRow = ({ totals }) => {
  const t = totals || {};
  const empty = !(t.jobCount || t.budget || t.cost || t.committed);
  const spentPct = t.budget ? t.cost / t.budget : null;
  const over = t.netOverUnder > 0.5;    // dollars over budget = bad
  const under = t.netOverUnder < -0.5;  // dollars under budget = good
  const netColor = empty ? 'var(--text-4)' : over ? 'var(--danger)' : under ? 'var(--success)' : 'var(--text-1)';
  const netSub = empty ? null : (
    <>
      <NetCount n={t.overBudget || 0} label="over" color="var(--danger)" />
      <span style={{ color: 'var(--text-4)' }}> · </span>
      <NetCount n={t.atRisk || 0} label="at risk" color="var(--warning)" />
      <span style={{ color: 'var(--text-4)' }}> · </span>
      <NetCount n={t.onTrack || 0} label="on track" color="var(--success)" />
    </>
  );
  // Blended portfolio gross margin (see totals memo). Only meaningful when the
  // filtered jobs carry a contract value; otherwise the tiles read "–".
  const hasContract = (t.contract || 0) > 0;
  const gm = t.grossMargin || 0;
  const gmPct = t.grossMarginPct;
  const gmColor = !hasContract ? 'var(--text-4)' : gm >= 0 ? 'var(--success)' : 'var(--danger)';

  return (
    <section className="pkr" role="group" aria-label="Portfolio totals" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: 0, padding: '4px 8px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
      <KpiTile label="Active Jobs" value={(t.jobCount || 0).toLocaleString('en-US')} muted={!t.jobCount} valueSize={34} variant="count" />
      <KpiDivider />
      <LedgerTile budget={t.budget || 0} cost={t.cost || 0} committed={t.committed || 0} spentPct={spentPct} empty={empty} />
      <KpiDivider />
      <KpiTile label="Net Over / Under" value={t.budget ? fmtSignedPct((t.netOverUnder || 0) / t.budget) : fmt$(t.netOverUnder || 0, { signed: true })} muted={empty} valueColor={netColor} sub={empty ? null : <><div style={{ fontSize: 17, fontWeight: 700, color: 'var(--text-2)', letterSpacing: '-0.02em', marginBottom: 3 }}>{fmt$(t.netOverUnder || 0, { signed: true })}</div><div>{netSub}</div></>} variant="net" />
      <KpiDivider />
      <GrossMarginTile gm={gm} gmPct={gmPct} contract={t.contract || 0} hasContract={hasContract} color={gmColor} gmCost={t.gmCost || 0} gmCommitted={t.gmCommitted || 0} gmJobs={t.gmJobs || 0} noContractJobs={(t.jobCount || 0) - (t.gmJobs || 0)} />
    </section>
  );
};

/* Job Costs → Warranty's tiles (Duncan 2026-10-19): a W job holds a finished job's warranty budget
   and what's spent against it -- no contract, so no margin. Left = budget − spent − committed. */
const WarrantyKpiRow = ({ jobs }) => {
  const sum = (k) => jobs.reduce((t, j) => t + parseFloat(j[k] || 0), 0);
  const budget = sum('total_budget');
  const cost = sum('total_cost');
  const committed = sum('total_committed');
  const left = budget - cost - committed;
  const empty = !jobs.length;
  return (
    <section className="pkr" role="group" aria-label="Warranty totals" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'stretch', gap: 0, padding: '4px 8px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
      <KpiTile label="Warranty Jobs" value={jobs.length.toLocaleString('en-US')} muted={empty} valueSize={34} variant="count" />
      <KpiDivider />
      <KpiTile label="Warranty Budget" value={fmt$(budget)} muted={empty} />
      <KpiDivider />
      <KpiTile label="Spent" value={fmt$(cost)} muted={empty} sub={budget ? `${fmtPct((cost / budget) * 100)} of budget` : null} />
      <KpiDivider />
      <KpiTile label="Committed" value={committed ? fmt$(committed) : '—'} muted={empty || !committed} valueColor={committed ? 'var(--warning)' : undefined} />
      <KpiDivider />
      <KpiTile label="Left" value={fmt$(left)} muted={empty} valueColor={empty ? undefined : left < -0.5 ? 'var(--danger)' : 'var(--success)'} sub={empty ? null : left < -0.5 ? 'over the warranty budget' : 'of the warranty budget'} />
    </section>
  );
};

/* ─────────────── "there is more below" ───────────────
   Ported from Facility Navigator's useScrollEdges/ScrollHint pair (apps/web/src/pages/
   CustomersPage.tsx). Only half of FN's pattern transfers, and it is worth saying which:

     · FN's ScrollHint — the words — transfers directly.
     · FN's scrollEdges inset shadow does NOT. That trick darkens the scroll container's
       own bottom edge, which works because FN's scroller is a dialog whose box edge IS
       the edge you see. Measured on the deployed page, this page's scroller is the
       WINDOW: `.app-main` and `.main-content` both declare overflow-y:auto but neither
       has a constrained height, so both size to their content (7517px, room = 0) and
       document.scrollingElement does all the scrolling. An inset shadow on .app-main
       would render 7500px down the page, at the end of the content, not at the fold.
     · FN's other warning still binds, so there is no gradient either: a fade pinned over
       the last rows read as a washed-out blank band there, twice. Nothing is painted over
       a job row — the cue is a small pill in the viewport's own bottom margin.

   Why it is needed at all: macOS Chrome draws overlay scrollbars, so an unscrolled page
   gives no indication whatsoever that 63 rows continue past the fold.

   useLayoutEffect + ResizeObserver on <body>, not a plain useEffect: the list GROWS after
   mount (the CE sync fills in 63 jobs, then the archived section), so a single
   post-render measurement would decide "nothing to scroll", hide the cue, and only a real
   scroll would correct it — backwards, since the cue is for people who have not scrolled. */
const useMoreBelow = () => {
  const [more, setMore] = useState(false);
  useLayoutEffect(() => {
    const measure = () => {
      const el = document.scrollingElement || document.documentElement;
      const room = el.scrollHeight - el.clientHeight;
      // > 24 so a page that overflows by a hairline does not earn a cue
      setMore(room > 24 && el.scrollTop < room - 2);
    };
    measure();
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      // Observe the element that actually GROWS, which is not the one that scrolls.
      // <body> and <html> are both viewport-sized here (862px at 1512x900) because the
      // shell's own wrappers are the tall ones — .app-main measures 7517px with 63 jobs
      // in it. Observing <body> meant the observer never fired at all: the cue stayed
      // hidden until a real scroll event, which is the exact backwards behaviour the
      // note above is about.
      const grows = document.querySelector('.app-main') || document.querySelector('.main-content') || document.body;
      ro.observe(grows);
    }
    return () => {
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, []);
  return more;
};

/* per-job collapsible card */
/* Column budget, and why these numbers. A card only gets `content width − 38` (its own
   border + 18px padding either side), and content width is `viewport − 300` with the
   sidebar out. The 2026-08-20 set (name ≥200, bar ≥176, four 124px number tracks, a
   116px badge, gap 14) needed 1108px — a 1446px window — so a 13" laptop (~1427) and
   every 1280-class Windows laptop scrolled each row sideways by 27px+, with the badge
   and chevron off the edge. Each track is now its measured need, not a round number:
     · Gross Margin / Over-Under 108: label ≈100px, a full-cent 8-figure sub-line
       ("+$12,345,678.90", 12px Geist Mono) is 105px.
     · Spent 104: a cost never carries a sign, so its widest sub-line is ≈101px.
     · Committed stays 124: its value is 15px, and an 8-figure figure is 121.8px.
     · Badge 104: the widest pill ("Under Budget") is 103px.
   The money tracks stay FIXED rather than auto/max-content: each card is its own grid,
   so content-sized tracks would resolve per-row and stop lining up down the list.
   Name and bar take what's left (the name truncates, the bar's caption adapts). With
   gap 12 the row fits a 1280px window (inner ≈925px) on one line; anything narrower
   still scrolls rather than clips. Committed stays: it is the only cent-accurate source
   on the row — the caption rounds $10,650.16 to "$11k". */
const HEADER_COLS = 'minmax(140px,1.6fr) minmax(126px,1.25fr) 108px 104px 124px 108px 104px 20px';
const HEADER_GAP = 12;
// Keep in sync with HEADER_COLS: minmax floors (140+126) + fixed tracks (108+104+124+108
// +104+20) + 7 gaps — the grid's real min-content, i.e. the width where it starts to scroll.
const HEADER_MIN = 140 + 126 + 108 + 104 + 124 + 108 + 104 + 20 + 7 * HEADER_GAP;
// In the job pop-up the expand arrow has nothing to do (the card is already open, and a click
// on the header closes it), so that column widens to clear the export and close buttons.
const HEADER_COLS_LIFT = HEADER_COLS.replace(/ 20px$/, ' 64px');
const HEADER_MIN_LIFT = HEADER_MIN + 44;
// A W job's third column is its Budget in dollars and cents, not a margin %: wide enough for
// $9,999,999.99 at 20px (Duncan 2026-10-20: DONNA-W-01's $184,553.00 ran into the bar).
const W_BUDGET_EXTRA = 48;
const warrantyCols = (cols) => cols.replace(' 108px ', ` ${108 + W_BUDGET_EXTRA}px `);
const MicroLabel = ({ children }) => <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)', whiteSpace: 'nowrap' }}>{children}</div>;
const MetricCol = ({ label, value, color, sub, big }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, alignItems: 'flex-end' }}>
    <MicroLabel>{label}</MicroLabel>
    <span className="mono" style={{ fontSize: big ? 20 : 15, lineHeight: big ? 1.1 : undefined, fontWeight: 700, color: color || 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>{value}</span>
    {/* Always drawn (blank when there's no sub) so every column is the same height
        and the four labels stay on one line across the card. */}
    <span className="mono" style={{ marginTop: -3, fontSize: 12, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{sub ?? '\u00a0'}</span>
  </div>
);

const JobCard = ({ job, project, open, onToggle, onOpenProject, expandedData, getNotes, addNote, bare, canSetContract, onSaveContract }) => {
  const phasesRef = useRef(null);
  const warranty = isWarrantyJob(job.job_id);
  // A P / E / M job with no project page: its contract is set here, inside the drop-down (admins).
  const contract = !project && job.jc_contract && onSaveContract
    ? { ...job.jc_contract, canEdit: !!canSetContract, onSave: (amount) => onSaveContract(job.job_id, amount) }
    : undefined;
  const budget = parseFloat(job.total_budget || 0);
  const cost = parseFloat(job.total_cost || 0);
  const committed = parseFloat(job.total_committed || 0);
  const overUnder = cost - budget;
  const pct = budget ? cost / budget : null;
  // Current gross margin — the same figure as the project's Gross Margin % tile
  // (buildFinancials: contract − (cost + open POs), ÷ contract). No contract → '—'.
  const fin = buildFinancials(job, project);
  const hasContract = fin.contract_value > 0;
  // #16: actual vs target — an arrow beside the actual (▲ above target, ▼ below) and the
  // target on hover. The target is contract − budget, so it needs a CE budget too.
  const gmDelta = hasContract && budget > 0 ? fin.actual_gm_pct - fin.gm_target : null;
  const gmVsTarget = gmDelta == null || Math.abs(gmDelta) < 0.0005 ? null : gmDelta > 0 ? 'above' : 'below';
  const gmTitle = gmDelta == null ? undefined
    : gmVsTarget ? `Target ${fmtPct(fin.gm_target * 100)} · ${fmtPct(Math.abs(gmDelta) * 100).replace('%', '')} points ${gmVsTarget}`
      : `On target (${fmtPct(fin.gm_target * 100)})`;
  // The target pops up the moment the mouse is over the Gross Margin column (no browser delay).
  const gmRef = useRef(null);
  const [gmHint, setGmHint] = useState(null); // { top, right } while hovering
  const showGmHint = () => {
    if (gmDelta == null || !gmRef.current) return;
    const r = gmRef.current.getBoundingClientRect();
    setGmHint({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
  };
  useEffect(() => {
    if (!gmHint) return undefined;
    const hide = () => setGmHint(null);
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [gmHint]);
  const name = project?.project_name || job.job_name || job.job_id;
  const number = project?.project_number || job.job_id;
  const customer = project?.client_name || '';

  return (
    <article style={{ background: bare ? 'transparent' : 'var(--bg-surface)', border: bare ? 'none' : '1px solid var(--border-default)', borderRadius: bare ? 0 : 12, boxShadow: bare ? 'none' : 'var(--shadow-sm)', overflow: 'hidden' }}>
      <div className="jc-jobhead" onClick={onToggle} role="button" aria-expanded={open} tabIndex={-1} style={{ cursor: 'pointer', padding: '15px 18px' }}>
        <div style={{ overflowX: 'auto' }}>
          <div style={{ display: 'grid', gap: HEADER_GAP, alignItems: 'center', minWidth: (bare ? HEADER_MIN_LIFT : HEADER_MIN) + (warranty ? W_BUDGET_EXTRA : 0), gridTemplateColumns: warranty ? warrantyCols(bare ? HEADER_COLS_LIFT : HEADER_COLS) : (bare ? HEADER_COLS_LIFT : HEADER_COLS) }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 0 }}>
              <span style={{ fontSize: 16.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.012em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                {warranty ? (
                  // A W job has no project page (Duncan 2026-10-19), so its number isn't a link.
                  <span className="mono" style={{ flex: '0 0 auto', whiteSpace: 'nowrap', fontSize: 13, fontWeight: 500, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>#{number}</span>
                ) : (
                <button type="button" className="jc-projlink" onClick={(e) => { e.stopPropagation(); onOpenProject && onOpenProject(project?.slug || project?.id || job.job_id); }} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: 0, flex: '0 0 auto', whiteSpace: 'nowrap', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--text-3)', background: 'none', border: 'none' }}>
                  <span className="mono" style={{ fontVariantNumeric: 'tabular-nums' }}>#{number}</span>
                  <span style={{ display: 'inline-flex', color: 'var(--text-4)' }}><JcIcon name="external" size={12} sw={1.7} /></span>
                </button>
                )}
                {customer && <><span style={{ color: 'var(--text-4)' }}>·</span><span style={{ fontSize: 13, color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>{customer}</span></>}
              </div>
            </div>
            <div onClick={(e) => e.stopPropagation()} style={{ cursor: 'default' }}>
              <BudgetBar budget={budget} cost={cost} committed={committed} />
            </div>
            {warranty ? (
              // A W job carries a finished job's warranty budget and no contract (Duncan 2026-10-19).
              <MetricCol big label="Budget" value={fmt$(budget)} sub="warranty" />
            ) : (
            <div ref={gmRef} onMouseEnter={showGmHint} onMouseLeave={() => setGmHint(null)}>
            <MetricCol big label="Gross Margin" value={hasContract ? (
              <span>
                {fmtPct(fin.actual_gm_pct * 100)}
                {gmVsTarget && (
                  <span role="img" aria-label={gmTitle} style={{ marginLeft: 4, fontSize: 11, verticalAlign: '0.25em', color: gmVsTarget === 'above' ? 'var(--success)' : 'var(--danger)' }}>
                    {gmVsTarget === 'above' ? '▲' : '▼'}
                  </span>
                )}
              </span>
            ) : '—'} color={!hasContract ? 'var(--text-4)' : fin.actual_gm >= 0 ? 'var(--success)' : 'var(--danger)'} sub={hasContract ? fmt$(fin.actual_gm, { signed: true }) : null} />
            </div>
            )}
            {gmHint && <TargetHint pos={gmHint} actualPct={fin.actual_gm_pct} targetPct={fin.gm_target} actualColor={fin.actual_gm >= 0 ? 'var(--success)' : 'var(--danger)'} />}
            <MetricCol big label="Spent" value={pct == null ? '—' : fmtPct(pct * 100)} color={spentColor(pct)} sub={fmt$(cost)} />
            <MetricCol label="Committed" value={committed ? fmt$(committed) : '—'} color={committed ? 'var(--warning)' : 'var(--text-4)'} />
            <MetricCol big label="Over / Under" value={fmtSignedPct(budget ? overUnder / budget : null)} color={overTone(overUnder)} sub={fmt$(overUnder, { signed: true })} />
            {/* Right-aligned like the money columns beside it, so the badges end on a
                common edge next to the chevron instead of leaving a ragged gutter. */}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}><HealthBadge pct={pct} size="sm" /></div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', color: 'var(--text-3)' }}>
              {!bare && <span style={{ display: 'inline-flex', transition: 'transform 180ms ease', transform: open ? 'rotate(180deg)' : 'none' }}><JcIcon name="chevron-d" size={18} /></span>}
            </div>
          </div>
        </div>
      </div>

      {open && (
        <div style={{ background: 'var(--bg-surface-2)', borderTop: '1px solid var(--border-subtle)', padding: '18px 18px 20px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {!expandedData ? (
            <div style={{ padding: '28px', display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: 24, height: 24, borderRadius: 999, border: '2.5px solid var(--border-default)', borderTopColor: 'var(--accent)', animation: 'jcSpin 0.7s linear infinite' }} />
            </div>
          ) : (
            <>
              <KpiCards fin={buildFinancials(job, project)} project={project} warranty={warranty} contract={contract} phases={expandedData.phases} collections={expandedData.collections} hours={expandedData.hours} endDate={expandedData.endDate} onSeeAllPhases={() => phasesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })} />
              <div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '0 2px 10px' }}>
                  <span style={{ fontSize: 13.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)' }}>Monthly Cost Trend</span>
                  <MonthlyLegend monthly={expandedData.monthly} />
                </div>
                <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 10, boxShadow: 'var(--shadow-xs)', padding: '14px 16px' }}>
                  <MonthlyChart monthly={expandedData.monthly} />
                </div>
                <MonthlyNote monthly={expandedData.monthly} />
              </div>
              <div ref={phasesRef}>
                <span style={{ display: 'block', fontSize: 13.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-3)', margin: '0 2px 10px' }}>Phase Breakdown</span>
                <PhaseTable phases={expandedData.phases} jobId={job.job_id} getNotes={getNotes} addNote={addNote} />
              </div>
            </>
          )}
        </div>
      )}
    </article>
  );
};

/* ═════════ Job-cost lift-in-blur ═════════
   Clicking a job lifts its card toward the viewer, blurs the rest of the page, and
   unfolds the expanded detail (KPIs + monthly chart + phase table) inside the lifted
   card — the same focus interaction as the AR notes pop-out. */
const JCL_CSS = `
.jcl-layer{position:fixed;inset:0;z-index:1000}
.jcl-backdrop{position:absolute;inset:0;background:color-mix(in oklch, var(--bg-page) 52%, transparent);-webkit-backdrop-filter:blur(7px) saturate(.9);backdrop-filter:blur(7px) saturate(.9);opacity:0;transition:opacity .42s cubic-bezier(.3,.6,.3,1)}
.jcl-layer.is-open .jcl-backdrop{opacity:1}
.jcl-layer.is-closing .jcl-backdrop{opacity:0;transition-duration:.3s}
.jcl-card{position:fixed;display:flex;flex-direction:column;border-radius:14px;background:var(--bg-surface);border:1px solid transparent;overflow:hidden;transform:translate3d(0,0,0) scale(1);box-shadow:0 1px 2px oklch(0 0 0/0),0 12px 24px -8px oklch(0 0 0/0),0 32px 64px -14px oklch(0 0 0/0);transition:transform .55s cubic-bezier(.3,1.32,.32,1),box-shadow .55s cubic-bezier(.3,1,.35,1),border-color .3s ease;will-change:transform,box-shadow;outline:none}
.jcl-layer.is-open .jcl-card{transform:translate3d(0,var(--jcl-dy,0px),0) scale(1.012);border-color:var(--border-default);box-shadow:var(--shadow-lift)}
.jcl-layer.is-closing .jcl-card{transition:transform .36s cubic-bezier(.35,.9,.3,1),box-shadow .36s ease,border-color .25s ease}
.jcl-scroll{overflow-y:auto;overscroll-behavior:contain;scrollbar-width:thin;scrollbar-color:var(--border-strong) transparent}
.jcl-scroll::-webkit-scrollbar{width:9px}
.jcl-scroll::-webkit-scrollbar-thumb{background:var(--border-default);border-radius:9px;border:2px solid transparent;background-clip:content-box}
.jcl-x{position:absolute;top:13px;right:13px;z-index:4;width:32px;height:32px;border-radius:8px;display:inline-flex;align-items:center;justify-content:center;color:var(--text-3);background:var(--bg-surface);border:1px solid var(--border-default);box-shadow:var(--shadow-xs);cursor:pointer;opacity:0;transform:scale(.8);transition:opacity .3s ease .1s,transform .35s cubic-bezier(.3,1.4,.35,1) .1s,color .15s,border-color .15s,background .15s}
.jcl-layer.is-open .jcl-x{opacity:1;transform:scale(1)}
.jcl-x:hover{color:var(--text-1);border-color:var(--border-strong);background:var(--bg-surface-2)}
.jcl-exp{position:absolute;top:13px;right:51px;z-index:5;opacity:0;transform:scale(0.8);transition:opacity .3s .1s,transform .35s cubic-bezier(.3,1.4,.35,1) .1s}
.jcl-layer.is-open .jcl-exp{opacity:1;transform:scale(1)}
@media (prefers-reduced-motion: reduce){.jcl-backdrop,.jcl-card,.jcl-x,.jcl-exp{transition-duration:.01ms!important;transition-delay:0s!important}}
`;
let jclInjected = false;
const injectJclCss = () => {
  if (jclInjected || typeof document === 'undefined' || document.getElementById('jcl-css')) { jclInjected = true; return; }
  const s = document.createElement('style'); s.id = 'jcl-css'; s.textContent = JCL_CSS;
  document.head.appendChild(s); jclInjected = true;
};
const jclMotion = () => (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) ? 0.05 : 1;

const JobCostLift = ({ job, project, rect, expandedData, getNotes, addNote, onOpenProject, returnFocus, onClosed, canSetContract, onSaveContract }) => {
  injectJclCss();
  const [state, setState] = useState('pre'); // pre → open → closing
  const stRef = useRef(state); stRef.current = state;
  const [dy, setDy] = useState(0);
  const [vh, setVh] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 900));
  // Portaled to <body>, outside the app's `.app.dark` container — re-apply the theme.
  const [isDark] = useState(() => typeof document !== 'undefined' && !!document.querySelector('.app.dark, .theme-dark'));
  const cardRef = useRef(null);

  // Clamp the lifted card into the viewport, then spring open. Re-runs once when the
  // detail loads (card grows) and whenever the viewport HEIGHT changes — depending on
  // the stable `!!expandedData` flag (not the freshly-rebuilt object) avoids churning
  // on every parent render.
  useLayoutEffect(() => {
    const M = 20;
    const h = cardRef.current ? cardRef.current.offsetHeight : rect.height;
    const overflow = (rect.top + h + M) - vh;
    setDy(overflow > 0 ? -Math.round(Math.min(overflow, Math.max(rect.top - M, 0))) : 0);
    let r2; const r1 = requestAnimationFrame(() => { r2 = requestAnimationFrame(() => setState((s) => (s === 'pre' ? 'open' : s))); });
    const fb = setTimeout(() => setState((s) => (s === 'pre' ? 'open' : s)), 120);
    return () => { cancelAnimationFrame(r1); if (r2) cancelAnimationFrame(r2); clearTimeout(fb); };
  }, [rect.top, rect.height, expandedData ? 1 : 0, vh]);

  // Move focus into the dialog once on mount.
  useEffect(() => {
    const t = setTimeout(() => { if (cardRef.current) cardRef.current.focus({ preventScroll: true }); }, 40);
    return () => clearTimeout(t);
  }, []);

  const close = useCallback(() => {
    if (stRef.current === 'closing') return;
    setState('closing');
    setTimeout(() => {
      onClosed();
      if (returnFocus) requestAnimationFrame(() => requestAnimationFrame(() => { try { returnFocus.focus({ preventScroll: true }); } catch (e) { /* non-focusable */ } }));
    }, Math.max(40, 360 * jclMotion()));
  }, [onClosed, returnFocus]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // Let an editable field (e.g. a phase note textarea) keep Escape so typing a
      // note and hitting Escape doesn't tear down the whole lift and lose the draft.
      // Same for a widget with something open of its own that marks itself
      // data-owns-escape (the Actual Cost "spent by phase" drill-down): one Esc
      // closes that first, the next closes the lift.
      const ae = document.activeElement;
      if (ae && (ae.tagName === 'TEXTAREA' || ae.tagName === 'INPUT' || ae.isContentEditable || ae.closest('[data-owns-escape="true"]'))) return;
      e.stopPropagation();
      close();
    };
    const w0 = window.innerWidth;
    const onResize = () => {
      // A width change means a real layout change → close. A height-only change
      // (mobile keyboard, window shorter) must NOT close, but must re-clamp so the
      // card's bottom stays reachable.
      if (window.innerWidth !== w0) close();
      else setVh(window.innerHeight);
    };
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', onResize);
    // Lock body scroll, compensating for the removed scrollbar so the blurred page
    // and the fixed card don't shift sideways on open/close.
    const prevOverflow = document.body.style.overflow;
    const prevPadR = document.body.style.paddingRight;
    const gutter = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = 'hidden';
    if (gutter > 0) document.body.style.paddingRight = gutter + 'px';
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', onResize);
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPadR;
    };
  }, [close]);

  // Keep focus inside the card while open.
  useEffect(() => {
    const onFocusIn = (ev) => {
      if (stRef.current !== 'open') return;
      const c = cardRef.current;
      if (c && !c.contains(ev.target)) c.focus({ preventScroll: true });
    };
    document.addEventListener('focusin', onFocusIn);
    return () => document.removeEventListener('focusin', onFocusIn);
  }, []);

  const maxH = Math.max(320, vh - 40);

  return createPortal(
    <div className={'jcl-layer' + (isDark ? ' theme-dark' : '') + (state === 'open' ? ' is-open' : '') + (state === 'closing' ? ' is-closing' : '')} style={{ '--jcl-dy': dy + 'px' }}>
      <div className="jcl-backdrop" onClick={close} />
      <div
        className="jcl-card"
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        tabIndex={-1}
        aria-label={'Job cost detail — ' + (project?.project_name || job.job_name || job.job_id)}
        style={{ left: rect.left, top: rect.top, width: rect.width, maxHeight: maxH }}
      >
        {/* PDF report or Excel workbook (PSR #14), beside the close button. */}
        {(project?.slug || project?.id) && <div className="jcl-exp"><PsrExportMenu project={project} variant="icon" /></div>}
        <button className="jcl-x" onClick={close} aria-label="Close job detail"><JcIcon name="x" size={16} sw={2} /></button>
        <div className="jcl-scroll">
          <JobCard job={job} project={project} open bare expandedData={expandedData} onToggle={close} onOpenProject={onOpenProject} getNotes={getNotes} addNote={addNote} canSetContract={canSetContract} onSaveContract={onSaveContract} />
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ─────────────── archived jobs ─────────────── */

/* #71 (Duncan 2026-10-19): an archived job is the SAME card as an active one -- the same columns,
   and a click opens the same detail ("the only difference between the active job tabs and the
   archive tabs is ... the job that I've been marked archived"). Each contract year is one group,
   newest first and open. No Restore and no Delete here: restore is on the Dashboard's Archived
   tab only, and nothing is ever deleted. */
const ArchivedYearGroup = ({ yearKey, count, defaultOpen = false, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 11, overflow: 'hidden' }}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="jc-archyear" aria-expanded={open} style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '11px 14px', cursor: 'pointer',
        background: 'transparent', border: 'none',
      }}>
        <span style={{ display: 'inline-flex', color: 'var(--text-3)', transition: 'transform 180ms ease', transform: open ? 'rotate(180deg)' : 'none' }}><JcIcon name="chevron-d" size={16} /></span>
        <span style={{ fontSize: 14.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.01em' }}>{yearKey}</span>
        <span className="mono" style={{ minWidth: 20, height: 20, padding: '0 7px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--text-3)', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>{count}</span>
      </button>
      {open && <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '0 12px 12px' }}>{children}</div>}
    </div>
  );
};

/* ─────────────── page ─────────────── */

const SORT_OPTIONS = [
  { value: 'overrun', label: 'Most Over Budget' },
  { value: 'budget', label: 'Largest Budget' },
  { value: 'spent', label: '% Spent' },
  { value: 'gm-high', label: 'Highest Gross Margin' },
  { value: 'gm-low', label: 'Lowest Gross Margin' },
  { value: 'name', label: 'Job Name' },
];

/* Job-type boxes (Duncan 2026-10-20): every job shows, but P (performance), E (engineering) and M
   (material only) jobs start hidden -- their boxes start ticked on each visit, like AR's "Hide credit
   memos and reversals". D and L jobs start shown. The type is the job number's middle letter
   (ANSON-D-02). W jobs are Warranty's own list, so no box. */
const TYPE_BOXES = [
  { letter: 'D', hidden: false },
  { letter: 'L', hidden: false },
  { letter: 'P', hidden: true },
  { letter: 'E', hidden: true },
  { letter: 'M', hidden: true },
];
const TYPES_HIDDEN_AT_START = Object.fromEntries(TYPE_BOXES.map((b) => [b.letter, b.hidden]));
const jobLetter = (id) => ((/-([A-Z])-\d+\s*$/i.exec(String(id || '')) || [])[1] || '').toUpperCase();

const JobCostDashboardPage = ({ onOpenProject, currentUser }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [jobSummaries, setJobSummaries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [lift, setLift] = useState(null); // { job, project, rect, returnFocus } — the lifted job pop-out
  const [phaseCache, setPhaseCache] = useState({});
  const [lineItemCache, setLineItemCache] = useState({});
  const [trendCache, setTrendCache] = useState({});
  const [notesCache, setNotesCache] = useState({});
  const [collectionsCache, setCollectionsCache] = useState({});
  const [hoursCache, setHoursCache] = useState({});
  const [hoursTrendCache, setHoursTrendCache] = useState({});
  const [invoicedCache, setInvoicedCache] = useState({});
  const [endDateCache, setEndDateCache] = useState({}); // schedule's last day, else End date; null = neither
  const [sortBy, setSortBy] = useState('overrun');
  const [filterHealth, setFilterHealth] = useState('all');
  const [filterPM, setFilterPM] = useState('');
  const [filterCM, setFilterCM] = useState('');
  const [hiddenTypes, setHiddenTypes] = useState(TYPES_HIDDEN_AT_START);
  const [contracts, setContracts] = useState({}); // a P / E / M job's contract set in Job Costs, by job number
  const canSetContract = can(currentUser, 'jobcosts.contracts'); // Admin + Accounting (Duncan 2026-10-20)
  const shownType = useCallback((id) => !hiddenTypes[jobLetter(id)], [hiddenTypes]);
  const [syncToast, setSyncToast] = useState(null);
  const moreBelow = useMoreBelow();
  const [archived, setArchived] = useState({});

  // quiet: reload behind the page instead of the full-page spinner.
  const loadData = useCallback(async (opts) => {
    const quiet = !!(opts && opts.quiet === true);
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const [currentData, monthlyData, projectList, archivedData, contractData] = await Promise.all([
        getSyncJobCostCurrentSummary({}).catch(() => []),
        getSyncJobCostSummary({}).catch(() => []),
        getProjects().catch(() => []),
        getSyncJobCostArchivedSummary().catch(() => ({})),
        getJobCostContracts().catch(() => null),
      ]);
      const summaries = (currentData && currentData.length > 0) ? currentData : (monthlyData || []);
      setJobSummaries(Array.isArray(summaries) ? summaries : []);
      setProjects(Array.isArray(projectList) ? projectList : projectList?.projects || []);
      setArchived(archivedData && typeof archivedData === 'object' ? archivedData : {});
      setContracts((contractData && contractData.contracts) || {});
    } catch (err) {
      setError(err.message || 'Failed to load job costs');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const loadDataRef = useRef(loadData);
  loadDataRef.current = loadData;
  const liftRef = useRef(null);
  liftRef.current = lift;
  useEffect(() => {
    const sseBase = process.env.NODE_ENV === 'production' ? '/api/sync/events' : 'http://localhost:5001/api/sync/events';
    const es = new EventSource(sseBase);
    es.addEventListener('sync-complete', (e) => {
      try {
        const data = JSON.parse(e.data);
        setPhaseCache({});
        setLineItemCache({});
        setTrendCache({});
        setHoursCache({});
        setHoursTrendCache({});
        loadDataRef.current();
        // Repopulate an open lift's detail so it doesn't get stuck on a spinner.
        const openJid = liftRef.current && liftRef.current.job && liftRef.current.job.job_id;
        if (openJid && loadJobDetailRef.current) loadJobDetailRef.current(openJid, true);
        setSyncToast(data.job_name || data.job_id || 'Job costs');
        setTimeout(() => setSyncToast(null), 5000);
      } catch (err) {
        console.error('SSE parse error:', err);
      }
    });
    es.onerror = () => {};
    return () => es.close();
  }, []);

  const projectLookup = useMemo(() => {
    const map = {};
    projects.forEach((p) => {
      if (p.project_number) map[p.project_number] = p;
      if (p.ce_job_id) map[p.ce_job_id] = p;
    });
    return map;
  }, [projects]);

  // force=true bypasses the cache guards — used to repopulate an open lift after a
  // background sync clears the caches (the guards would otherwise see the stale,
  // still-populated closure and skip the refetch).
  const loadJobDetail = useCallback(async (jobId, force) => {
    if (force || !phaseCache[jobId]) {
      try { const phases = await getSyncJobCostPhases({ job_id: jobId, source: 'current' }); setPhaseCache((prev) => ({ ...prev, [jobId]: phases || [] })); } catch (err) { console.error('Phase load failed:', err); }
    }
    if (force || !lineItemCache[jobId]) {
      try { const raw = await getSyncJobCostsCurrent(jobId); setLineItemCache((prev) => ({ ...prev, [jobId]: applyBudgetAssumption(raw, jobId) })); } catch (err) { console.error('Line item load failed:', err); }
    }
    if (force || !trendCache[jobId]) {
      try { const trend = await getSyncJobCostMonthlySummary(jobId); setTrendCache((prev) => ({ ...prev, [jobId]: trend || [] })); } catch (err) { console.error('Trend load failed:', err); }
    }
    if (force || !notesCache[jobId]) {
      try { const notes = await getCostCodeNotes(jobId); setNotesCache((prev) => ({ ...prev, [jobId]: notes || [] })); } catch (err) { console.error('Notes load failed:', err); }
    }
    if (force || !(jobId in collectionsCache)) {
      // null = unavailable (no AR access / mirror down); the tile says so instead of 0%.
      try { const col = await getArJobCollections(jobId); setCollectionsCache((prev) => ({ ...prev, [jobId]: col })); } catch (err) { setCollectionsCache((prev) => ({ ...prev, [jobId]: null })); }
    }
    if (force || !(jobId in hoursCache)) {
      try { const hrs = await getJobHoursCurrent(jobId); setHoursCache((prev) => ({ ...prev, [jobId]: hrs || [] })); } catch (err) { setHoursCache((prev) => ({ ...prev, [jobId]: null })); }
    }
    if (force || !(jobId in hoursTrendCache)) {
      try { const ht = await getJobHoursMonthly(jobId); setHoursTrendCache((prev) => ({ ...prev, [jobId]: ht || [] })); } catch (err) { setHoursTrendCache((prev) => ({ ...prev, [jobId]: null })); }
    }
    if (force || !(jobId in invoicedCache)) {
      try { const inv = await getArInvoicedMonthly(jobId); setInvoicedCache((prev) => ({ ...prev, [jobId]: inv || [] })); } catch (err) { setInvoicedCache((prev) => ({ ...prev, [jobId]: null })); }
    }
    if (force || !(jobId in endDateCache)) {
      // The Contract Value tile's due line: the schedule's last day, else the project's End
      // date (the header's order). No Summit project ⇒ neither; a failed read stays
      // undefined so the tile shows nothing rather than "no schedule yet".
      const proj = projectLookup[jobId];
      try { const sum = proj ? await getProjectSummary(proj.id) : null; setEndDateCache((prev) => ({ ...prev, [jobId]: (sum && sum.latest_task_finish) || (proj && proj.end_date) || null })); } catch (err) { setEndDateCache((prev) => ({ ...prev, [jobId]: undefined })); }
    }
  }, [phaseCache, lineItemCache, trendCache, notesCache, collectionsCache, hoursCache, hoursTrendCache, invoicedCache, endDateCache, projectLookup]);
  const loadJobDetailRef = useRef(loadJobDetail);
  loadJobDetailRef.current = loadJobDetail;

  /* Click a job → measure its card and lift it into the blurred pop-out. Measure
     synchronously (React nulls e.currentTarget after the handler returns). */
  const openLift = useCallback((job, e) => {
    const head = e && e.currentTarget;
    const article = head && head.closest && head.closest('article');
    if (!article) return;
    const b = article.getBoundingClientRect();
    const proj = projectLookup[job.job_id];
    loadJobDetail(job.job_id);
    setLift((cur) => cur || { job, project: proj, rect: { left: b.left, top: b.top, width: b.width, height: b.height }, returnFocus: head });
  }, [loadJobDetail, projectLookup]);

  const getNotesFor = (jobId, phaseNo, code) =>
    (notesCache[jobId] || [])
      .filter((n) => String(n.phase) === String(phaseNo) && String(n.category || '') === String(code || ''))
      .map(normalizeNote);

  const addNoteFor = async (jobId, phaseNo, code, text) => {
    const created = await addCostCodeNote({ job_id: jobId, phase: phaseNo, category: code || '', note: text });
    setNotesCache((prev) => ({ ...prev, [jobId]: [...(prev[jobId] || []), created] }));
  };

  // People filters — match each job to its linked project's assigned people
  // (project_manager can hold several names; construction_manager one).
  // The same test filters the Archived view.
  const matchesPeople = useCallback((jobId) => {
    const proj = projectLookup[jobId];
    if (filterPM) {
      const pms = (proj?.project_manager || '').split(',').map((s) => s.trim());
      if (!pms.includes(filterPM)) return false;
    }
    if (filterCM) {
      const cms = (proj?.construction_manager || '').split(',').map((s) => s.trim());
      if (!cms.includes(filterCM)) return false;
    }
    return true;
  }, [filterPM, filterCM, projectLookup]);
  // The top bar's search (lib/pageSearch): job number, name, client, PM, CM.
  const [search, setSearch] = usePageSearchState('Search job costs…');
  const matchesText = useCallback((row) => {
    const proj = projectLookup[row.job_id];
    return matchesSearch(search, row.job_id, row.job_name, proj?.project_name, proj?.client_name,
      proj?.project_manager, proj?.construction_manager);
  }, [search, projectLookup]);
  // A P / E / M job with no project page carries the contract set in Job Costs (+ CE change orders) as
  // contract_value -- buildFinancials, the portfolio tiles and the Gross Margin sort read it like a
  // project's -- and the whole entry as jc_contract, for its Contract Value tile.
  const withContract = useCallback((j) => {
    const c = contracts[String(j.job_id || '').trim().toUpperCase()];
    return c && !projectLookup[j.job_id] ? { ...j, contract_value: c.value, jc_contract: c } : j;
  }, [contracts, projectLookup]);
  const jobRows = useMemo(() => jobSummaries.map(withContract), [jobSummaries, withContract]);
  const saveContract = useCallback(async (jobId, amount) => {
    const r = await setJobCostContract(jobId, amount);
    setContracts((prev) => ({ ...prev, [r.job_number]: { set: r.set, change_orders: r.change_orders, cos: r.cos, value: r.value } }));
  }, []);

  const personFilteredJobs = useMemo(
    () => jobRows.filter((j) => (!filterPM && !filterCM ? true : matchesPeople(j.job_id)) && matchesText(j)),
    [jobRows, filterPM, filterCM, matchesPeople, matchesText]
  );

  // Distinct assignable people, drawn from the projects behind jobs that have
  // cost data (so the dropdowns only list people actually on the board).
  const pmOptions = useMemo(() => {
    const set = new Set();
    jobSummaries.forEach((j) => (projectLookup[j.job_id]?.project_manager || '').split(',').map((s) => s.trim()).filter(Boolean).forEach((n) => set.add(n)));
    return [{ value: '', label: 'All PMs' }, ...[...set].sort().map((n) => ({ value: n, label: n }))];
  }, [jobSummaries, projectLookup]);
  const cmOptions = useMemo(() => {
    const set = new Set();
    jobSummaries.forEach((j) => (projectLookup[j.job_id]?.construction_manager || '').split(',').map((s) => s.trim()).filter(Boolean).forEach((n) => set.add(n)));
    return [{ value: '', label: 'All CMs' }, ...[...set].sort().map((n) => ({ value: n, label: n }))];
  }, [jobSummaries, projectLookup]);

  // The set the filter bar selects — status filter on top of the people filter;
  // drives BOTH the KPI tiles and the list, so the five portfolio tiles reflect
  // exactly what's filtered, not the whole book.
  // W jobs are their own list, Job Costs → Warranty: never in All / Over Budget / At Risk / On Track
  // or the portfolio tiles. A job type whose Hide box is ticked is out of all of them too.
  const mainJobs = useMemo(
    () => personFilteredJobs.filter((j) => !isWarrantyJob(j.job_id) && shownType(j.job_id)),
    [personFilteredJobs, shownType]
  );
  const visibleJobs = useMemo(
    () => (filterHealth === 'all' ? mainJobs : mainJobs.filter((j) => filterBucket(ratioOf(j)) === filterHealth)),
    [mainJobs, filterHealth]
  );

  const totals = useMemo(() => {
    const r = { budget: 0, cost: 0, committed: 0, contract: 0, jobCount: visibleJobs.length, overBudget: 0, atRisk: 0, onTrack: 0,
      gmCost: 0, gmCommitted: 0, gmJobs: 0 };
    let gmCostBase = 0; // cost + open POs, summed ONLY over contract-bearing jobs
    visibleJobs.forEach((j) => {
      const jCost = parseFloat(j.total_cost || 0);
      const jCommitted = parseFloat(j.total_committed || 0);
      r.budget += parseFloat(j.total_budget || 0);
      r.cost += jCost;
      r.committed += jCommitted;
      const proj = projectLookup[j.job_id];
      const contract = parseFloat(proj?.revised_contract_value || proj?.contract_amount || j.contract_value || 0);
      if (contract > 0) { r.contract += contract; gmCostBase += jCost + jCommitted; r.gmCost += jCost; r.gmCommitted += jCommitted; r.gmJobs += 1; }
      const bucket = filterBucket(ratioOf(j));
      if (bucket === 'over') r.overBudget++;
      else if (bucket === 'at-risk') r.atRisk++;
      else r.onTrack++;
    });
    r.netOverUnder = r.cost - r.budget;
    // Blended portfolio gross margin: contract − (cost + open POs), matching the
    // per-job Gross Margin tiles (buildFinancials). Computed over jobs that HAVE a
    // contract (numerator + denominator use the same set) so a job with spend but
    // no contract entered can't silently deflate the margin. The % is revenue-
    // weighted (total margin ÷ total contract), NOT a mean of per-job percentages.
    r.grossMargin = r.contract - gmCostBase;
    r.grossMarginPct = r.contract > 0 ? r.grossMargin / r.contract : null;
    return r;
  }, [visibleJobs, projectLookup]);

  // Sort by the "Sort by" choice; the Archived view sorts each year the same way.
  const sortJobs = useCallback((jobs) => {
    const list = [...jobs];
    if (sortBy === 'overrun') list.sort((a, b) => (healthRank(a) - healthRank(b)) || (overrunOf(b) - overrunOf(a)));
    else if (sortBy === 'budget') list.sort((a, b) => parseFloat(b.total_budget || 0) - parseFloat(a.total_budget || 0));
    else if (sortBy === 'spent') list.sort((a, b) => (ratioOf(b) || 0) - (ratioOf(a) || 0));
    else if (sortBy === 'name') list.sort((a, b) => String(a.job_name || '').localeCompare(String(b.job_name || '')));
    else if (sortBy === 'gm-high' || sortBy === 'gm-low') {
      // By the Gross Margin % each row shows (buildFinancials). A job with no
      // contract has no margin, so it goes last in both directions.
      const gm = new Map(list.map((j) => {
        const f = buildFinancials(j, projectLookup[j.job_id]);
        return [j, f.contract_value > 0 ? f.actual_gm_pct : null];
      }));
      const dir = sortBy === 'gm-high' ? -1 : 1;
      list.sort((a, b) => {
        const ga = gm.get(a), gb = gm.get(b);
        if (ga == null || gb == null) return (ga == null) - (gb == null);
        return dir * (ga - gb);
      });
    }
    return list;
  }, [sortBy, projectLookup]);
  const filteredJobs = useMemo(() => sortJobs(visibleJobs), [visibleJobs, sortJobs]);

  // #71: every archived job (the server buckets them by archive year) under its CONTRACT year,
  // as on the Dashboard, with the same people / search filters and sort as the active list.
  const archivedRows = useMemo(() => Object.values(archived || {}).filter(Array.isArray).flat().map(withContract), [archived, withContract]);
  const archivedGroups = useMemo(
    () => groupByContractYear(sortJobs(archivedRows.filter((r) => matchesPeople(r.job_id) && matchesText(r) && shownType(r.job_id))),
      (r) => projectLookup[r.job_id]),
    [archivedRows, matchesPeople, matchesText, shownType, sortJobs, projectLookup]
  );
  const archivedTotal = useMemo(() => archivedGroups.reduce((sum, g) => sum + g.rows.length, 0), [archivedGroups]);
  const showArchived = filterHealth === 'archived';
  const showWarranty = filterHealth === 'warranty';
  const warrantyJobs = useMemo(() => sortJobs(personFilteredJobs.filter((j) => isWarrantyJob(j.job_id))), [personFilteredJobs, sortJobs]);

  // One job's card -- the same for an active job and an archived one (#71); a click lifts it
  // into the detail pop-out.
  const renderJobCard = (job) => {
    const project = projectLookup[job.job_id];
    const isLifted = lift && lift.job.job_id === job.job_id;
    return (
      <div key={job.job_id} data-job-id={job.job_id} style={{ visibility: isLifted ? 'hidden' : undefined }}>
        <JobCard
          job={job}
          project={project}
          open={false}
          onToggle={(e) => openLift(job, e)}
          onOpenProject={onOpenProject}
          expandedData={null}
        />
      </div>
    );
  };

  // /job-costs?job=EAGLE-P-06 (the Accounting dashboard opens a Job Costs-only job here): show that
  // job -- its type's box unticked, its tab, the search narrowed to it -- then lift its card open.
  const [params, setParams] = useSearchParams();
  const linkedJob = String(params.get('job') || '').trim().toUpperCase();
  const pendingLift = useRef(null);
  useEffect(() => {
    if (!linkedJob || loading) return;
    const row = jobRows.find((j) => String(j.job_id).toUpperCase() === linkedJob)
      || archivedRows.find((j) => String(j.job_id).toUpperCase() === linkedJob);
    const next = new URLSearchParams(params); next.delete('job'); setParams(next, { replace: true });
    if (!row) return;
    setHiddenTypes((h) => ({ ...h, [jobLetter(row.job_id)]: false }));
    setFilterHealth(isWarrantyJob(row.job_id) ? 'warranty' : archivedRows.includes(row) ? 'archived' : 'all');
    setSearch(row.job_id);
    pendingLift.current = { row, tries: 0 };
  }, [linkedJob, loading, jobRows, archivedRows, params, setParams, setSearch]);
  useEffect(() => {
    const want = pendingLift.current;
    if (!want) return;
    const head = document.querySelector(`[data-job-id="${String(want.row.job_id).replace(/"/g, '')}"] .jc-jobhead`);
    if (head) { pendingLift.current = null; openLift(want.row, { currentTarget: head }); return; }
    want.tries += 1;
    if (want.tries > 5) pendingLift.current = null; // in a closed archive year: the narrowed list is enough
  });

  // Status-tab counts reflect the active people filter (but not the status itself),
  // so each tab shows how many of the selected people's jobs fall in that bucket.
  const counts = useMemo(() => ({
    all: mainJobs.length,
    over: mainJobs.filter((j) => filterBucket(ratioOf(j)) === 'over').length,
    'at-risk': mainJobs.filter((j) => filterBucket(ratioOf(j)) === 'at-risk').length,
    'on-track': mainJobs.filter((j) => filterBucket(ratioOf(j)) === 'on-track').length,
    warranty: warrantyJobs.length,
    archived: archivedTotal,
  }), [mainJobs, warrantyJobs, archivedTotal]);


  if (loading) return <LoadingState message="Loading job costs…" />;

  // One button, two homes: the filter bar on the normal path, the standalone header on
  // the error/empty states (which render no filter bar). In the bar it is icon-only —
  // measured: the labelled button needs ~94px and the bar has ~100px of slack at 1512
  // only once the label is dropped. With it, a tenant whose PM names run long (the
  // selects size to their widest option — a 22-character name makes each ~198px) tipped
  // the bar onto a second line, spending 46px of the 48px the action row gave back.
  const refresh = (inBar) => (
    <button
      type="button"
      className="jc-refresh"
      onClick={loadData}
      title={inBar ? 'Refresh job costs' : undefined}
      aria-label={inBar ? 'Refresh job costs' : undefined}
      style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, height: 32, width: inBar ? 32 : undefined, padding: inBar ? 0 : '0 13px', borderRadius: 8, fontSize: 14, fontWeight: 500, color: 'var(--text-2)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer', flex: '0 0 auto' }}
    >
      <JcIcon name="refresh" size={13} />{inBar ? null : ' Refresh'}
    </button>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`
        @keyframes jcSpin { to { transform: rotate(360deg); } }
        /* Portfolio KPI row — "Quiet Ledger": one calm surface panel with hairline
           dividers; on narrow widths it dissolves into a tidy neutral card grid
           (same family as the expanded-job KpiCards) so wrapping never strands a divider. */
        /* Portfolio sums reach 9 figures (full-cent), which is what stopped the single
           hairline row of 4 tiles fitting below ~1600px. But the 2x2 fallback pays for
           a WIDTH problem in HEIGHT — 129 + 129 = 258px for the same eleven figures the
           one-row band shows in 129px — and it charges that to laptops, the screens
           with the least height to spare. So laptops keep the one-row band and we make
           the figures fit instead: the binding constraint is Net Over/Under, whose
           15-glyph full-cent value needs 222px at 26px and 183px at 21px, against the
           ~265-300px a quarter of the row gives at 1360-1599px. The 2x2 stays, but only
           below 1360px where 4-across genuinely cannot fit. >=1600px is untouched. */
        /* Floor measured, not guessed: at 1360 all four tiles still render every
           figure on one line (checked against the widest content each holds — the
           Net Over/Under sub-line and Gross Margin's 15-glyph Contract value — and
           re-checked with three-digit health counts, which also fit). Below 1360 the
           2x2 takes over. */
        @media (max-width: 1599px) and (min-width: 1360px) {
          .pkr { padding: 2px 6px !important; }
          .pkr .pkr-tile { padding: 12px 14px !important; min-width: 0 !important; }
          .pkr .pkr-div { margin: 10px 0 !important; }
          /* Width follows need, not flex-grow accident. Measured on the deployed page
             at 1512: the two ledger tiles took 335px each (grow 1.55) while Net
             Over/Under got 258px (grow 1) — 234px of inner width against the 237px
             its sub-line "5 over · 14 at risk · 44 on track" needs, so it wrapped to
             two lines. The ledger tiles' widest row ("Budget  $120,755,723.93") needs
             ~191px, so they had ~120px spare EACH. Pinning the two hero tiles and
             letting the ledgers absorb all variation gives 160/364/300/364: the sub
             clears with 39px to spare (enough for three-digit counts) at every width
             in the band, and Active Jobs stops reserving 258px for a 2-digit number. */
          .pkr .pkr-tile-count { flex: 0 1 160px !important; }
          .pkr .pkr-tile-net { flex: 0 1 300px !important; }
          .pkr .pkr-hero { font-size: 21px !important; }
          .pkr .pkr-hero-lg { font-size: 27px !important; }
          .pkr .pkr-ledger { font-size: 13.5px !important; }
        }
        /* The gap needs !important here: the section carries an inline "gap: 0" for
           the hairline-row layout, and inline styles outrank a plain declaration.
           Without it the four tiles in this grid render jammed edge-to-edge — which is
           what they did, despite this rule having asked for 10px all along. */
        @media (max-width: 1359px) {
          .pkr { display: grid !important; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px !important; padding: 0 !important; background: transparent !important; border: none !important; box-shadow: none !important; }
          .pkr .pkr-div { display: none; }
          .pkr .pkr-tile { min-width: 0 !important; background: var(--bg-surface); border: 1px solid var(--border-default); border-radius: 10px; box-shadow: var(--shadow-xs); }
        }
        @media (max-width: 640px) { .pkr { grid-template-columns: 1fr !important; } }
        /* Centred on the CONTENT column, not the viewport. A fixed element cannot
           inherit .main-content's margin-left, so the sidebar footprint is added back
           by hand: half of 252px expanded, half of 84px collapsed, and nothing below
           768px where App.css takes the sidebar off-canvas and zeroes the margin. */
        .jc-more { position: fixed; left: calc(50% + 126px); bottom: 18px; z-index: 890; transform: translateX(-50%);
                   display: inline-flex; align-items: center; gap: 6px; height: 26px; padding: 0 12px;
                   border-radius: 999px; font-family: var(--font-sans); font-size: 11.5px; font-weight: 600;
                   letter-spacing: 0.01em; color: var(--accent-text); background: var(--accent-soft);
                   border: 1px solid color-mix(in oklch, var(--accent) 22%, transparent);
                   box-shadow: var(--shadow-sm); pointer-events: none;
                   animation: jcMoreIn 220ms cubic-bezier(.3,.9,.35,1) both; }
        @keyframes jcMoreIn { from { opacity: 0; transform: translate(-50%, 6px); } to { opacity: 1; transform: translate(-50%, 0); } }
        .sidebar-collapsed .jc-more { left: calc(50% + 42px); }
        @media (max-width: 768px) { .jc-more { left: 50%; } }
        @media (prefers-reduced-motion: reduce) { .jc-more { animation-duration: .01ms; } }
        .jc-refresh:hover { border-color: color-mix(in oklch, var(--accent) 35%, var(--border-default)); color: var(--accent-text); background: var(--accent-soft); }
        .jc-jobhead:hover { background: color-mix(in oklch, var(--bg-hover) 45%, transparent); }
        .jc-projlink:hover, .jc-projlink:hover span { color: var(--accent-text) !important; }
        .j-note:hover { background: var(--accent-soft) !important; color: var(--accent-text) !important; }
        .j-note-ta:focus { border-color: var(--accent) !important; box-shadow: 0 0 0 3px var(--accent-ring) !important; }
        .jc-sort:focus-visible { outline: none; border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-ring); }
        .jc-archyear:hover { background: color-mix(in oklch, var(--bg-hover) 45%, transparent); }
        .jc-restore:hover:not(:disabled) { border-color: color-mix(in oklch, var(--accent) 35%, var(--border-default)); background: var(--accent-soft); }
        .jc-check { display: inline-flex; align-items: center; gap: 8px; height: 32px; padding: 0 12px; border-radius: 8px; font: 500 14px var(--font-sans);
          color: var(--text-1); background: var(--bg-surface); border: 1px solid var(--border-default); box-shadow: var(--shadow-xs); cursor: pointer; white-space: nowrap; user-select: none; }
        .jc-check input { width: 15px; height: 15px; margin: 0; accent-color: var(--accent); cursor: pointer; }
        .jc-kpi-edit:hover { border-color: color-mix(in oklch, var(--accent) 45%, var(--border-default)) !important; color: var(--accent-text) !important; }
        .jc-kpi-edit:focus-visible { outline: none; box-shadow: 0 0 0 3px var(--accent-ring); }
        .jc-check:has(input:focus-visible) { border-color: var(--accent); box-shadow: 0 0 0 3px var(--accent-ring); }
        .jc-health { display: flex; max-width: 100%; overflow-x: auto; scrollbar-width: none; }
        .jc-health::-webkit-scrollbar { display: none; }
        .jc-health [role="tab"] { white-space: nowrap; }
        .jc-archdel:hover:not(:disabled) { border-color: color-mix(in oklch, var(--danger) 45%, var(--border-default)); background: var(--danger-soft); }
      `}</style>

      {/* Refresh + the sync chip live in the filter bar's right-hand cluster on the
          normal path. They used to sit in a dedicated action row, which spent 48px of
          vertical (32px header + 16px flex gap) on one 32px button and no words — the
          page title "Job Costs" is already in AppHeader. On a 14" screen that 48px is
          half a job row. The row survives only for the error and empty states, which
          have no filter bar to host the button. */}
      {(error || (jobSummaries.length === 0 && archivedAll.length === 0)) && (
        <header style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '0 2px' }}>
          <div style={{ flex: 1, minWidth: 0 }} />
          {refresh(false)}
        </header>
      )}

      {error ? (
        <StateScreen kind="server-error" title="Couldn’t load job costs" body="Something went wrong fetching the ComputerEase cost sync. Check your connection and try again." primary={{ label: 'Retry', onClick: loadData }} />
      ) : jobSummaries.length === 0 && archivedAll.length === 0 ? (
        <StateScreen kind="empty" title="No ComputerEase job costs found" body="Once jobs sync from ComputerEase, their budgets, costs, and phase breakdowns will appear here." />
      ) : (
        <>
          {/* gap 12 (was 16): the bar now also carries Refresh, and it must still lay
              out on one line at laptop widths or the 48px reclaimed above is spent
              again on a wrap. */}
          <section style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 12px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
            {/* Five options (#18 added Archived) outgrow a phone: there the row scrolls sideways. */}
            <div className="jc-health">
            <SegmentedControl
              value={filterHealth}
              onChange={setFilterHealth}
              options={[
                { value: 'all', label: 'All', count: counts.all },
                { value: 'over', label: 'Over Budget', count: counts.over },
                { value: 'at-risk', label: 'At Risk', count: counts['at-risk'] },
                { value: 'on-track', label: 'On Track', count: counts['on-track'] },
                { value: 'warranty', label: 'Warranty', count: counts.warranty },
                { value: 'archived', label: 'Archived', count: counts.archived },
              ]}
            />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto', flexWrap: 'wrap' }}>
              <SortSelect value={filterPM} onChange={setFilterPM} options={pmOptions} />
              <SortSelect value={filterCM} onChange={setFilterCM} options={cmOptions} />
              {(filterPM || filterCM) && (
                <button
                  type="button"
                  onClick={() => { setFilterPM(''); setFilterCM(''); }}
                  style={{ height: 32, padding: '0 11px', borderRadius: 8, fontSize: 13.5, fontWeight: 500, color: 'var(--text-3)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)', cursor: 'pointer' }}
                >
                  Clear
                </button>
              )}
            </div>
            <div role="group" aria-label="Job types" style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto', flexWrap: 'wrap' }}>
              {TYPE_BOXES.map(({ letter }) => (
                <label key={letter} className="jc-check">
                  <input type="checkbox" checked={!!hiddenTypes[letter]} onChange={(e) => setHiddenTypes((h) => ({ ...h, [letter]: e.target.checked }))} />
                  Hide {letter} jobs
                </label>
              ))}
            </div>
            <div style={{ flex: 1, minWidth: 8 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, flex: '0 0 auto' }}>
              <span style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-3)' }}>Sort by</span>
              <SortSelect value={sortBy} onChange={setSortBy} options={SORT_OPTIONS} />
              {refresh(true)}
            </div>
          </section>

          {!showArchived && !showWarranty && <PortfolioKpiRow totals={totals} />}
          {showWarranty && <WarrantyKpiRow jobs={warrantyJobs} />}

          {showArchived ? (
            archivedGroups.length === 0 && search.trim() ? (
              <StateScreen kind="empty" {...noMatchText(search)} />
            ) : archivedGroups.length === 0 ? (
              <StateScreen kind="empty" title="No archived jobs" body="Archived jobs are kept here with all their data, filed under their contract year. Restore one from the Dashboard's Archived tab." />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {archivedGroups.map((g, i) => (
                  <ArchivedYearGroup key={g.key} yearKey={g.key} count={g.rows.length} defaultOpen={i === 0}>
                    {g.rows.map(renderJobCard)}
                  </ArchivedYearGroup>
                ))}
              </div>
            )
          ) : showWarranty ? (
            warrantyJobs.length === 0 && search.trim() ? (
              <StateScreen kind="empty" {...noMatchText(search)} />
            ) : warrantyJobs.length === 0 ? (
              <StateScreen kind="empty" title="No warranty jobs" body="When a job ends its warranty budget moves to a W job; W jobs on the Job Cost roster show here." />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {warrantyJobs.map(renderJobCard)}
              </div>
            )
          ) : filteredJobs.length === 0 && search.trim() ? (
            <StateScreen kind="empty" {...noMatchText(search)} />
          ) : filteredJobs.length === 0 ? (
            <StateScreen kind="empty" title="No jobs match this filter" body="Try a different health filter, or switch back to All to see every ComputerEase job." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {filteredJobs.map(renderJobCard)}
            </div>
          )}
        </>
      )}

      {/* Scroll cue. Only while the page actually has somewhere to go — it hides the
          moment you reach the bottom, and never renders when 63 jobs happen to fit. */}
      {moreBelow && !lift && (
        <div className="jc-more" aria-hidden="true">▾ more below</div>
      )}

      {/* Sync chip, floated out of the flow. It is a 5-second SSE notification
          (setSyncToast + a 5000ms clear) roughly 290px wide for a typical job name.
          It used to live in the action row, whose flex:1 spacer swallowed it; in the
          filter bar — which wraps — its arrival and departure would have shoved every
          job row down 46px and back twice a sync. Fixed, bottom-right, and below the
          lift's z-index so a focused card still covers it. */}
      {syncToast && (
        <div style={{ position: 'fixed', right: 24, bottom: 24, zIndex: 900, pointerEvents: 'none' }}>
          <SyncChip label={`${syncToast} — refreshed`} when="just now" />
        </div>
      )}

      {lift && (() => {
        const jid = lift.job.job_id;
        // The job's current row (a contract saved in the pop-out shows at once), else the one clicked.
        const liftRow = jobRows.find((j) => j.job_id === jid) || archivedRows.find((j) => j.job_id === jid) || lift.job;
        const ready = phaseCache[jid] && lineItemCache[jid] && trendCache[jid];
        const liftExpandedData = ready
          ? { phases: buildPhases(phaseCache[jid], lineItemCache[jid]), monthly: buildMonthly(trendCache[jid], hoursTrendCache[jid], invoicedCache[jid]), collections: collectionsCache[jid], hours: hoursCache[jid], endDate: endDateCache[jid] }
          : null;
        return (
          <JobCostLift
            job={liftRow}
            project={lift.project}
            rect={lift.rect}
            returnFocus={lift.returnFocus}
            expandedData={liftExpandedData}
            onOpenProject={onOpenProject}
            getNotes={(phaseNo, code) => getNotesFor(jid, phaseNo, code)}
            addNote={(phaseNo, code, text) => addNoteFor(jid, phaseNo, code, text)}
            onClosed={() => setLift(null)}
            canSetContract={canSetContract}
            onSaveContract={saveContract}
          />
        );
      })()}
    </div>
  );
};

export default JobCostDashboardPage;
